import { ComponentProps } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import { useMemoViewStore } from "@/zapi-frontend/stores/memoView";

function NarrowLayoutIcon(props: ComponentProps<"svg">) {
  return (
    <svg viewBox="0 0 14.6667 11.3333" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
      <path
        d="M12.6667 10.3333V11.3333H2V10.3333H12.6667ZM13.6667 9.33333V2C13.6667 1.44772 13.219 1 12.6667 1H2C1.44772 1 1 1.44772 1 2V9.33333C1 9.88562 1.44772 10.3333 2 10.3333V11.3333L1.89714 11.3307C0.87444 11.2789 0.0544169 10.4589 0.00260417 9.4362L0 9.33333V2C0 0.895431 0.895431 3.22128e-08 2 0H12.6667C13.7712 0 14.6667 0.895431 14.6667 2V9.33333C14.6667 10.4034 13.8263 11.2772 12.7695 11.3307L12.6667 11.3333V10.3333C13.219 10.3333 13.6667 9.88562 13.6667 9.33333Z"
        fill="currentColor"
      />
      <path
        d="M4.66667 3.33333C4.66667 2.96514 4.96514 2.66667 5.33333 2.66667H9.33333C9.70152 2.66667 10 2.96514 10 3.33333V8C10 8.36819 9.70152 8.66667 9.33333 8.66667H5.33333C4.96514 8.66667 4.66667 8.36819 4.66667 8V3.33333Z"
        fill="currentColor"
      />
    </svg>
  );
}

function WideLayoutIcon(props: ComponentProps<"svg">) {
  return (
    <svg viewBox="0 0 14.6667 11.3333" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
      <path
        d="M12.6667 10.3333V11.3333H2V10.3333H12.6667ZM13.6667 9.33333V2C13.6667 1.44772 13.219 1 12.6667 1H2C1.44772 1 1 1.44772 1 2V9.33333C1 9.88562 1.44772 10.3333 2 10.3333V11.3333L1.89714 11.3307C0.87444 11.2789 0.0544169 10.4589 0.00260417 9.4362L0 9.33333V2C0 0.895431 0.895431 3.22128e-08 2 0H12.6667C13.7712 0 14.6667 0.895431 14.6667 2V9.33333C14.6667 10.4034 13.8263 11.2772 12.7695 11.3307L12.6667 11.3333V10.3333C13.219 10.3333 13.6667 9.88562 13.6667 9.33333Z"
        fill="currentColor"
      />
      <path
        d="M2.66667 3.33333C2.66667 2.96514 2.96514 2.66667 3.33333 2.66667H11.3333C11.7015 2.66667 12 2.96514 12 3.33333V8C12 8.36819 11.7015 8.66667 11.3333 8.66667H3.33333C2.96514 8.66667 2.66667 8.36819 2.66667 8V3.33333Z"
        fill="currentColor"
      />
    </svg>
  );
}

type TLayoutToggleProps = {
  disabled?: boolean;
};

export function LayoutToggle({ disabled }: TLayoutToggleProps) {
  const { t } = useTranslation("projectHome");
  const { isFullWidth, setFullWidth } = useMemoViewStore();

  const buttonClass = (active: boolean) =>
    cn(
      "flex h-7 w-8 items-center justify-center rounded-md transition-colors disabled:pointer-events-none disabled:opacity-50",
      active ? "bg-white text-innoveraZinc-900 shadow-sm" : "text-innoveraZinc-500 hover:text-innoveraZinc-700"
    );

  return (
    <div className="flex items-center gap-0.5 rounded-lg border border-innoveraZinc-150 bg-innoveraZinc-100 p-0.5">
      <button
        type="button"
        title={t("chapterHeader.fullWidth")}
        aria-label={t("chapterHeader.fullWidth")}
        aria-pressed={isFullWidth}
        disabled={disabled}
        onClick={() => setFullWidth(true)}
        className={buttonClass(isFullWidth)}
        data-testid="memo-layout-full"
      >
        <WideLayoutIcon className="h-4 w-4" />
      </button>
      <button
        type="button"
        title={t("chapterHeader.defaultWidth")}
        aria-label={t("chapterHeader.defaultWidth")}
        aria-pressed={!isFullWidth}
        disabled={disabled}
        onClick={() => setFullWidth(false)}
        className={buttonClass(!isFullWidth)}
        data-testid="memo-layout-default"
      >
        <NarrowLayoutIcon className="h-4 w-4" />
      </button>
    </div>
  );
}
